import { devLog } from 'utils/dev-utils';

import type { WorkerPayload } from './types';

/** Given a web worker instance, return a function that returns a Promise. */
export const promisifyWorker = <T, U>(worker: Worker) => {
  const callbacks = new Map<
    number,
    { resolve: (value: U) => void; reject: (reason?: unknown) => void; startTime: number }
  >();
  let messageId = 0;

  worker.onmessage = (event) => {
    const { id, data } = event.data as WorkerPayload<U>;
    const callback = callbacks.get(id);
    if (callback) {
      const duration = performance.now() - callback.startTime;
      devLog(`Worker task ${id} resolved in ${duration.toFixed(2)} ms`);
      callback.resolve(data);
      callbacks.delete(id);
    }
  };

  worker.onmessageerror = (event) => {
    const { id } = event.data as WorkerPayload<U>;
    const callback = callbacks.get(id);
    if (callback) {
      const duration = performance.now() - callback.startTime;
      devLog(`Worker task ${id} rejected in ${duration.toFixed(2)} ms`);
      callback.reject(JSON.stringify(event));
      callbacks.delete(id);
    }
  };

  worker.onerror = (err) => {
    callbacks.forEach((callback, id) => {
      const duration = performance.now() - callback.startTime;
      devLog(`Worker task ${id} errored in ${duration.toFixed(2)} ms`);
      callback.reject(String(err));
    });
    callbacks.clear();
  };

  return (data: T): Promise<U> => {
    return new Promise((resolve, reject) => {
      const id = messageId++;
      const startTime = performance.now();
      callbacks.set(id, { resolve, reject, startTime });
      worker.postMessage({ id, data } as WorkerPayload<T>);
    });
  };
};
